import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@services/baseQuery';
import {
  BillOutResultSet,
  ChargingStationGetRoomSelectList,
  ChargingStationSlipList,
  IComputedBreakdown,
} from '../../modules/front-office/interface/charging-station';
import { handleError } from '../../utils/api-error-handling';
import { encryptPayload } from '../../utils/security';

export const chargingStationApi = createApi({
  reducerPath: 'chargingStationApi',
  baseQuery: baseQuery,
  tagTypes: ['ChargingStation'],
  endpoints: (builder) => ({
    getRoomSelectList: builder.query({
      query: () => {
        return `chargingstation/room-select`;
      },
      providesTags: ['ChargingStation'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: ChargingStationGetRoomSelectList) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
      keepUnusedDataFor: 0.0001,
    }),
    postBillOut: builder.mutation({
      query: (payload) => ({
        url: 'chargingstation/billout',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['ChargingStation'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: BillOutResultSet) => {
        return response;
      },
    }),
    getChargingStationList: builder.query({
      query: (query) => {
        return `chargingstation/list${query}`;
      },
      providesTags: ['ChargingStation'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: ChargingStationSlipList) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
      keepUnusedDataFor: 0.0001,
    }),
    voidSlip: builder.mutation({
      query: (payload) => ({
        url: 'chargingstation/void',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['ChargingStation'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    getComputedBreakdown: builder.mutation({
      query: (payload) => {
        return {
          url: 'chargingstation/compute-breakdown',
          method: 'POST',
          body: encryptPayload(payload),
        };
      },
      invalidatesTags: ['ChargingStation'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: IComputedBreakdown) => {
        return response;
      },
    }),
  }),
});

export const {
  useLazyGetRoomSelectListQuery,
  usePostBillOutMutation,
  useLazyGetChargingStationListQuery,
  useVoidSlipMutation,
  useGetComputedBreakdownMutation,
} = chargingStationApi;
